// Uniform packing for the two eye-lift shaders; offsets must match their WGSL structs.

import type { ExtentMultipliers } from './eyeChart.ts'
import type { Vector3 } from '../math/index.ts'

// eye_position_map.wgsl Params: one map-texel count plus padding to 16 bytes.
export const POSITION_MAP_PARAMS_BYTES = 16

export function packPositionMapParams(mapTexels: number): ArrayBuffer {
  const buffer = new ArrayBuffer(POSITION_MAP_PARAMS_BYTES)
  new Uint32Array(buffer)[0] = mapTexels
  return buffer
}

// The shader's fixed light-array size; past it collectSceneLights drops the dimmest.
export const MAX_EYE_LIGHTS = 8

// eye_lift.wgsl Params: 8 u32 + 7 vec4 + 8 direction vec4 + 8 radiance vec4.
export const LIFT_PARAMS_BYTES = 400

// Float32 slots in the lift params block — see the WGSL struct for the layout.
const SLOT_CENTRE = 8
const SLOT_AXIS = 12
const SLOT_CAMERA = 16
const SLOT_TANGENT_EXTENT = 20
const SLOT_NORMAL_EXTENT = 24
const SLOT_SHAPE_SCALARS = 28
const SLOT_SPECULAR_SCALARS = 32
const SLOT_LIGHT_DIRS = 36
const SLOT_LIGHT_COLORS = SLOT_LIGHT_DIRS + MAX_EYE_LIGHTS * 4

export interface EyeLiftParams {
  numTexels: number
  resolution: number
  colorChannels: number
  outOffset: number
  capacity: number
  centre: Vector3
  axis: Vector3
  radius: number
  /** The chart's one meridional step (the exporter's `dtheta`). */
  meridionalStep: number
  tangent: ExtentMultipliers
  normal: ExtentMultipliers
  opacityBias: number
  specVisBias: number
  specGain: number
  /** meta.bsigGuard — the guarded-tanh blend weight behind spec_vis. */
  bsigGuard: number
  /** meta.spec.sgNorm — RGCA's SG normalizer, (sqrt2)*pi^(2/3). */
  sgNorm: number
  /** Absent → the shader skips the specular term entirely. */
  cameraCentre?: Vector3 | null
  /** The live scene's lights (eyeSceneLights). Empty → no specular, same as no camera. */
  lights: { directions: ArrayLike<number>, colors: ArrayLike<number>, count: number }
}

export function packEyeLiftParams(params: EyeLiftParams): ArrayBuffer {
  const lightCount = params.lights.count | 0
  if (lightCount > MAX_EYE_LIGHTS) {
    throw new Error(`eye lift: ${lightCount} specular lights exceeds the shader's `
      + `${MAX_EYE_LIGHTS} — raise MAX_EYE_LIGHTS and the arrays in eye_lift.wgsl together`)
  }
  const buffer = new ArrayBuffer(LIFT_PARAMS_BYTES)
  const counts = new Uint32Array(buffer)
  const values = new Float32Array(buffer)
  const useSpecular = !!params.cameraCentre && params.colorChannels >= 4 && lightCount > 0

  counts[0] = params.numTexels
  counts[1] = params.resolution
  counts[2] = params.colorChannels
  counts[3] = params.outOffset
  counts[4] = params.capacity
  counts[5] = lightCount
  counts[6] = useSpecular ? 1 : 0
  // counts[7] is padding: the 8 leading u32 exist so the first vec4 lands on 32 bytes.

  writeVector(values, SLOT_CENTRE, params.centre)
  writeVector(values, SLOT_AXIS, params.axis)
  writeVector(values, SLOT_CAMERA, params.cameraCentre ?? [0, 0, 0])
  writeMultipliers(values, SLOT_TANGENT_EXTENT, params.tangent)
  writeMultipliers(values, SLOT_NORMAL_EXTENT, params.normal)

  values[SLOT_SHAPE_SCALARS] = params.radius
  values[SLOT_SHAPE_SCALARS + 1] = params.meridionalStep
  values[SLOT_SHAPE_SCALARS + 2] = params.bsigGuard
  values[SLOT_SHAPE_SCALARS + 3] = params.opacityBias
  values[SLOT_SPECULAR_SCALARS] = params.specVisBias
  values[SLOT_SPECULAR_SCALARS + 1] = params.specGain
  values[SLOT_SPECULAR_SCALARS + 2] = params.sgNorm

  // Directions and radiances in two parallel vec4 arrays; the w lanes are std140 padding.
  for (let light = 0; light < lightCount; light++) {
    writeVector(values, SLOT_LIGHT_DIRS + light * 4, readTriple(params.lights.directions, light))
    writeVector(values, SLOT_LIGHT_COLORS + light * 4, readTriple(params.lights.colors, light))
  }
  return buffer
}

function writeVector(values: Float32Array, slot: number, vector: Vector3): void {
  values[slot] = vector[0]
  values[slot + 1] = vector[1]
  values[slot + 2] = vector[2]
}

function writeMultipliers(values: Float32Array, slot: number,
                         multipliers: ExtentMultipliers): void {
  values[slot] = multipliers.init
  values[slot + 1] = multipliers.floor
  values[slot + 2] = multipliers.cap
}

function readTriple(source: ArrayLike<number>, index: number): Vector3 {
  return [source[index * 3], source[index * 3 + 1], source[index * 3 + 2]]
}

// Per-texel statics interleaved into one buffer (TEXEL_* in eye_lift.wgsl).
export const TEXEL_STRIDE = 4

export function packTexelStatics(chart: {
  numTexels: number
  uvGrid: Float32Array
  azimuthalStep: Float32Array
  specularSigma: Float32Array
}): Float32Array {
  const packed = new Float32Array(chart.numTexels * TEXEL_STRIDE)
  for (let texel = 0; texel < chart.numTexels; texel++) {
    const offset = texel * TEXEL_STRIDE
    packed[offset] = chart.uvGrid[texel * 2]
    packed[offset + 1] = chart.uvGrid[texel * 2 + 1]
    packed[offset + 2] = chart.azimuthalStep[texel]
    packed[offset + 3] = chart.specularSigma[texel]
  }
  return packed
}
